import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { hasValue } from '@frontend/sports/common/core/utils/extended-types';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { Story, SubStory } from '@frontend/sports/types/components/story-content';
import { StoryContentPayload } from '@frontend/sports/types/components/widget/types';
import { LocalStoreService } from '@frontend/vanilla/core';
import { isEqual } from 'lodash-es';
import { Observable, Subject, distinctUntilChanged } from 'rxjs';

import { Promotion } from '../crm-offer-data/crm-offer.model';
import { CrmPromotionProviderService } from '../crm-promotion/crm-promotion-provider.service';
import {
    ContentStory,
    ContentSubStory,
    ImageSubStory,
    NavigationPayload,
    PromotionSubStory,
    SubStoryType,
    VideoSubStory,
} from './story-content.model';

@Injectable({ providedIn: 'root' })
export class StoryContentService {
    private promotions: Promotion[];
    private playingStatus$ = new Subject<boolean>();
    private nextAndPreviousActionSubject = new Subject<NavigationPayload>();
    private readonly VIEWED_STORIES_KEY = 'StoryContentViewedData';
    private readonly DISMISSED_MODULES_KEY = 'StoryContentDismissedModules';

    private dismissedModules: Map<string, undefined>;
    private viewedStories: Map<string, undefined>;
    private viewedStories$ = new Subject<void>();
    private content$ = new Subject<void>();

    constructor(
        private localStorage: LocalStoreService,
        promotionProvider: CrmPromotionProviderService,
        productUtils: SportsProductUtils,
    ) {
        this.loadDismissed();
        this.loadViewed();
        promotionProvider.promotions$
            .pipe(
                productUtils.filterSportsEmitLast(),
                distinctUntilChanged((previous, current) => isEqual(previous, current)),
                takeUntilDestroyed(),
            )
            .subscribe((promotions) => this.promotionsChanged(promotions));
    }

    isDismissed(id: string): boolean {
        return this.dismissedModules.has(id);
    }

    setDismissed(id: string): void {
        if (this.isDismissed(id)) {
            return;
        }
        this.dismissedModules.set(id, undefined);
        this.saveDismissed();
    }

    isViewed(item: StoryContentPayload | Story | SubStory): boolean {
        if (!item) {
            return false;
        }

        if ('stories' in item) {
            return item.stories.every((story) => this.isViewed(story));
        }

        if ('subStories' in item) {
            return item.subStories.every((subStory) => this.isViewed(subStory));
        }

        return this.viewedStories.has(item.id);
    }

    setViewed(item: SubStory): void {
        if (this.isViewed(item)) {
            return;
        }

        this.viewedStories.set(item.id, undefined);
        this.saveViewed();

        this.viewedStories$.next();
    }

    get viewedChange(): Observable<void> {
        return this.viewedStories$.asObservable();
    }

    get contentChange(): Observable<void> {
        return this.content$.asObservable();
    }

    get navigationChange(): Observable<NavigationPayload> {
        return this.nextAndPreviousActionSubject.asObservable();
    }

    get statusChange(): Observable<boolean> {
        return this.playingStatus$.asObservable();
    }

    navigate(action: NavigationPayload): void {
        this.nextAndPreviousActionSubject.next(action);
    }

    play(): void {
        this.playingStatus$.next(false);
    }

    pause(): void {
        this.playingStatus$.next(true);
    }

    map(stories: Story[]): ContentStory[] {
        return stories
            .map((story) => this.mapStory(story))
            .filter(hasValue)
            .map((story) => ({ story, isViewed: this.isViewed(story) }))
            .sort((first, second): number => Number(first.isViewed) - Number(second.isViewed))
            .map((story) => story.story);
    }

    private mapStory(story: Story): ContentStory | undefined {
        const subStories = story.subStories.map((subStory) => this.mapSubStory(subStory)).filter(hasValue);

        if (subStories.length) {
            return {
                ...story,
                subStories,
            };
        }

        return;
    }

    private mapSubStory(subStory: SubStory): ContentSubStory | undefined {
        subStory = {
            ...subStory,
            image: this.getResizedImageUrl(subStory.image, false),
            ctaPopupHeaderImage: this.getResizedImageUrl(subStory.ctaPopupHeaderImage, true),
            crmPromotionBackground: this.getResizedImageUrl(subStory.crmPromotionBackground, false),
        };

        if (subStory.crmPromotionId?.length) {
            const promotion = this.promotions?.find((promo) => promo.id === subStory.crmPromotionId);

            if (promotion) {
                if (!subStory.crmPromotionBackground && promotion.banner?.backgroundImageUrl) {
                    subStory.crmPromotionBackground = this.getResizedImageUrl(promotion.banner.backgroundImageUrl, false);
                }

                return <PromotionSubStory>{
                    ...subStory,
                    promotion,
                    type: SubStoryType.Promotion,
                };
            }
        }

        if (subStory.externalVideoUrl || subStory.videoUrl?.includes('.mp4')) {
            return <VideoSubStory>{
                ...subStory,
                type: SubStoryType.Video,
            };
        }

        if (subStory.image) {
            return <ImageSubStory>{
                ...subStory,
                type: SubStoryType.Image,
            };
        }

        return undefined;
    }

    private loadDismissed(): void {
        const dismissedModules = this.localStorage.get<string[]>(this.DISMISSED_MODULES_KEY) ?? [];
        this.dismissedModules = new Map(dismissedModules.map((module) => [module, undefined]));
    }

    private saveDismissed(): void {
        this.localStorage.set<string[]>(this.DISMISSED_MODULES_KEY, [...this.dismissedModules.keys()]);
    }

    private loadViewed(): void {
        const viewedStories = this.localStorage.get<string[]>(this.VIEWED_STORIES_KEY) ?? [];
        this.viewedStories = new Map(viewedStories.map((story) => [story, undefined]));
    }

    private saveViewed(): void {
        this.localStorage.set<string[]>(this.VIEWED_STORIES_KEY, [...this.viewedStories.keys()]);
    }

    private promotionsChanged(promotions: Promotion[]): void {
        this.promotions = promotions.filter((p) => p.banner);
        this.content$.next();
    }

    private getResizedImageUrl(url: string | undefined, isWideImage: boolean): string {
        if (!url) {
            return '';
        }

        const queryParamPrefix = url.includes('?') ? '&' : '?';
        const appendValue = isWideImage ? 'p=width720' : 'p=width540';

        return `${url}${queryParamPrefix}${appendValue}`;
    }
}
